import { useCallback, useState } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import _ from "lodash";

import Button from "components/Button";
import Dialog from "components/Dialog";
import toast from "components/Toast";
import { useLoadingBackdrop } from "components/useLoadingBackdrop";

import { TRANSACTION_VALIDATION_SCHEMA } from "pages/Deal/FinancingEvents/constants";
import { ETransactionDialogStatus } from "pages/Deal/FinancingEvents/constants";
import TransactionForm from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionForm";
import { getTransactionTypeFromTransactionOrFormValues } from "pages/Deal/FinancingEvents/TransactionModifyDialog/TransactionForm/TransactionGeneralInfoForm/utils";
import {
  handleTransactionInvalidRequest,
  handleTransactionTypeChange,
} from "pages/Deal/FinancingEvents/utils";

import { ETransactionType } from "entities/Deal/constants";
import { ITransactionValuesForm } from "entities/Deal/sdk";
import {
  financingProgramCreate,
  transactionCreate,
  useDealDetails,
  useFinancingEvents,
  useTransactions,
} from "entities/Deal/sdk";

export interface ITransactionCreateDialogButton {
  initialValues: ITransactionValuesForm;
  financingEventId: number;
}

const TransactionCreateDialogButton = ({
  initialValues,
  financingEventId,
}: ITransactionCreateDialogButton) => {
  const [transactionDialogStatus, setTransactionDialogStatus] =
    useState<ETransactionDialogStatus>(ETransactionDialogStatus.CLOSED);

  const [previousTransactionType, setPreviousTransactionType] =
    useState<ETransactionType | null>(
      getTransactionTypeFromTransactionOrFormValues({
        transactionInfo: initialValues,
      })
    );

  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  const [showLoading, hideLoading] = useLoadingBackdrop();
  const { data: deal } = useDealDetails({ dealId });

  const { mutate: mutateTransactions } = useTransactions({
    dealId,
    financingEventId,
  });

  const { mutate: mutateFinancingEvents } = useFinancingEvents({
    dealId,
  });

  const createTransaction = useCallback(
    (data: ITransactionValuesForm, formik) => {
      transactionCreate({ dealId, data, financingEventId })
        .then(() => {
          setTransactionDialogStatus(ETransactionDialogStatus.CLOSED);
          toast.successMessage("Transaction created successfully!");
          mutateTransactions();
          mutateFinancingEvents();
          formik.resetForm();
          setPreviousTransactionType(null);
        })
        .catch(handleTransactionInvalidRequest);
    },
    [dealId, financingEventId, mutateFinancingEvents, mutateTransactions]
  );

  const handleCreateSubmit = useCallback(
    async (data: ITransactionValuesForm, formik) => {
      showLoading();
      if (
        _.isNil(data.financing_program_id) &&
        !_.isNil(data.financing_program_name)
      ) {
        const response = await financingProgramCreate({
          data: { name: data.financing_program_name },
        });
        data.financing_program_id = response.id;
      }
      createTransaction(data, formik);
      hideLoading();
    },
    [hideLoading, showLoading, createTransaction]
  );

  const formik = useFormik({
    initialValues: { ...initialValues, capital_provider: deal?.company.name },
    validationSchema: TRANSACTION_VALIDATION_SCHEMA,
    enableReinitialize: true,
    onSubmit: handleCreateSubmit,
  });

  const onDialogClose = useCallback(() => {
    setTransactionDialogStatus(ETransactionDialogStatus.CLOSED);
    formik.resetForm();
  }, [formik, setTransactionDialogStatus]);

  const onBackButtonClickHandler = useCallback(() => {
    setTransactionDialogStatus(ETransactionDialogStatus.GENERAL_INFO);
  }, [setTransactionDialogStatus]);

  const resetOldTransactionTypeOnCreateStepAdvance = useCallback(() => {
    if (formik.values.transaction_type !== "") {
      setPreviousTransactionType(
        formik.values.transaction_type as ETransactionType
      );
    }

    if (
      _.isNil(previousTransactionType) ||
      formik.values.transaction_type !== previousTransactionType
    ) {
      handleTransactionTypeChange({
        transactionType: formik.values.transaction_type as ETransactionType,
        setFieldValue: formik.setFieldValue,
      });
    }
  }, [formik, previousTransactionType, setPreviousTransactionType]);

  const onNextButtonClickHandler = useCallback(() => {
    resetOldTransactionTypeOnCreateStepAdvance();
    formik.validateForm().then((errors) => {
      if (_.isNil(errors.transaction_type) && _.isNil(errors.amount)) {
        setTransactionDialogStatus(ETransactionDialogStatus.SPECIFIC_INFO);
      }
      formik.setFieldTouched("transaction_type", true, true);
      formik.setFieldTouched("amount", true, true);
    });
  }, [
    formik,
    setTransactionDialogStatus,
    resetOldTransactionTypeOnCreateStepAdvance,
  ]);

  return (
    <>
      <Button
        sx={{ width: "200px" }}
        onClick={() =>
          setTransactionDialogStatus(ETransactionDialogStatus.GENERAL_INFO)
        }
      >
        + New transaction
      </Button>

      <Dialog
        open={transactionDialogStatus !== ETransactionDialogStatus.CLOSED}
        onClose={onDialogClose}
        fullWidth
      >
        <TransactionForm
          formik={formik}
          transactionDialogStatus={transactionDialogStatus}
          previousTransactionType={previousTransactionType}
          onDialogClose={onDialogClose}
          onBackButtonClickHandler={onBackButtonClickHandler}
          onNextButtonClickHandler={onNextButtonClickHandler}
          lastUpdatedInformation={null}
        />
      </Dialog>
    </>
  );
};

export default TransactionCreateDialogButton;
